import React, { useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {
  Plus,
  Home,
  MapPin,
  Bed,
  Bath,
  Car,
  DollarSign,
  Sparkles,
  PenLine,
  Search,
  Image as ImageIcon,
  Building2,
  Ruler,
  Trees,
  Zap,
  Dog,
  Navigation,
  Brain,
  FileText,
  Video,
  Settings2,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Eye,
  Edit3,
  X,
  ExternalLink,
  Calendar,
  Save,
  ShoppingCart,
  Key,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

// Mock listings data for display
const mockListings = [
  {
    id: "1",
    status: "active",
    basic_info: {
      headline: "Stunning Family Home with Ocean Views",
      address: "123 Bondi Rd, Bondi NSW 2026",
      suburb: "Bondi",
      property_type: "House",
    },
    specifications: {
      bedrooms: 4,
      bathrooms: 2,
      car_spaces: 2,
    },
    pricing_and_financials: {
      display_price: "$1,850,000",
    },
    media: {
      primary_image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800&auto=format&fit=crop&q=60",
    },
  },
  {
    id: "2",
    status: "active",
    basic_info: {
      headline: "Modern Apartment in Heart of Sydney",
      address: "45 George St, Sydney NSW 2000",
      suburb: "Sydney CBD",
      property_type: "Apartment",
    },
    specifications: {
      bedrooms: 2,
      bathrooms: 1,
      car_spaces: 1,
    },
    pricing_and_financials: {
      display_price: "$950,000",
    },
    media: {
      primary_image: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&auto=format&fit=crop&q=60",
    },
  },
  {
    id: "3",
    status: "under-offer",
    basic_info: {
      headline: "Charming Terrace with City Views",
      address: "78 Crown St, Surry Hills NSW 2010",
      suburb: "Surry Hills",
      property_type: "Terrace",
    },
    specifications: {
      bedrooms: 3,
      bathrooms: 2,
      car_spaces: 0,
    },
    pricing_and_financials: {
      display_price: "$1,450,000",
    },
    media: {
      primary_image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&auto=format&fit=crop&q=60",
    },
  },
];

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    active: "bg-green-500/20 text-green-400 border-green-500/30",
    "under-offer": "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    sold: "bg-red-500/20 text-red-400 border-red-500/30",
    withdrawn: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    "off-market": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  };

  const statusLabels = {
    active: "Active",
    "under-offer": "Under Offer",
    sold: "Sold",
    withdrawn: "Withdrawn",
    "off-market": "Off Market",
  };

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-mono border ${
        statusStyles[status] || statusStyles.active
      }`}
    >
      {statusLabels[status] || status}
    </span>
  );
};

// Listing Card Component
const ListingCard = ({ listing, onView, onEdit, isMockData = false }) => {
  // Safe accessors with defaults
  const basicInfo = listing.basic_info || {};
  const specifications = listing.specifications || {};
  const pricingAndFinancials = listing.pricing_and_financials || {};
  const media = listing.media || {};

  return (
    <div className="glass rounded-xl overflow-hidden border border-white/10 hover:border-[#00D1FF]/30 transition-all duration-300 group">
      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden">
        {media.primary_image ? (
          <img
            src={media.primary_image}
            alt={basicInfo.headline || "Property"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-white/5 flex items-center justify-center">
            <Home className="w-12 h-12 text-[#9CA3AF]/50" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <StatusBadge status={listing.status} />
        </div>
        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-2 py-1 rounded text-xs font-mono text-[#F3F4F6]">
          {basicInfo.property_type || "Property"}
        </div>
        {isMockData && (
          <div className="absolute bottom-3 left-3 bg-purple-500/80 backdrop-blur-sm px-2 py-1 rounded text-xs font-mono text-white">
            Sample
          </div>
        )}
        {listing.transaction_type && (
          <div className={`absolute bottom-3 ${isMockData ? 'left-20' : 'left-3'} backdrop-blur-sm px-2 py-1 rounded text-xs font-mono text-white ${
            listing.transaction_type === "rent" ? "bg-orange-500/80" : "bg-emerald-500/80"
          }`}>
            {listing.transaction_type === "rent" ? "For Rent" : "For Sale"}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-heading font-bold text-lg text-[#F3F4F6] mb-2 line-clamp-1">
          {basicInfo.headline || "Untitled Listing"}
        </h3>
        <div className="flex items-center gap-1 text-[#9CA3AF] text-sm mb-3">
          <MapPin className="w-4 h-4 text-[#00D1FF]" />
          <span className="line-clamp-1">{basicInfo.address || "Address not specified"}</span>
        </div>

        {/* Specs */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-1 text-[#9CA3AF]">
            <Bed className="w-4 h-4" />
            <span className="text-sm">{specifications.bedrooms ?? 0}</span>
          </div>
          <div className="flex items-center gap-1 text-[#9CA3AF]">
            <Bath className="w-4 h-4" />
            <span className="text-sm">{specifications.bathrooms ?? 0}</span>
          </div>
          <div className="flex items-center gap-1 text-[#9CA3AF]">
            <Car className="w-4 h-4" />
            <span className="text-sm">{specifications.car_spaces ?? 0}</span>
          </div>
        </div>

        {/* Price and Actions */}
        <div className="flex items-center justify-between">
          <span className="font-heading font-bold text-xl text-[#00D1FF]">
            {pricingAndFinancials.display_price || "Contact Agent"}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onView && onView(listing)}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] transition-all"
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
            {!isMockData && (
              <button
                onClick={() => onEdit && onEdit(listing)}
                className="p-2 rounded-lg bg-white/5 hover:bg-[#00D1FF]/20 text-[#9CA3AF] hover:text-[#00D1FF] transition-all"
                title="Edit Listing"
              >
                <Edit3 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to check if a value is empty
const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (typeof value === "number" && (isNaN(value) || value === 0)) return false; // 0 is valid
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

// Helper function to parse API error messages
const parseApiError = (error) => {
  const detail = error?.response?.data?.detail;
  
  if (!detail) {
    return "An unexpected error occurred. Please try again.";
  }
  
  // If detail is a string, return it directly
  if (typeof detail === "string") {
    return detail;
  }
  
  // If detail is an array (FastAPI validation errors), extract messages
  if (Array.isArray(detail)) {
    const messages = detail.map((err) => {
      if (typeof err === "string") return err;
      if (err && typeof err === "object" && err.msg) {
        // Extract field location if available
        const loc = err.loc ? err.loc.slice(1).join(".") : "";
        return loc ? `${loc}: ${err.msg}` : err.msg;
      }
      return "Validation error";
    });
    return messages.join(", ");
  }
  
  // If detail is an object with a message property
  if (typeof detail === "object" && detail.msg) {
    return detail.msg;
  }
  
  return "An unexpected error occurred. Please try again.";
};

// Form Input Component with highlight support
const FormInput = ({ label, required, error, icon: Icon, className = "", highlight = false, ...props }) => (
  <div className={`space-y-2 ${className}`}>
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
      {highlight && (
        <span className="ml-auto flex items-center gap-1 text-yellow-400 text-xs">
          <AlertCircle className="w-3 h-3" />
          Missing
        </span>
      )}
    </label>
    <input
      {...props}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : highlight
          ? "border-yellow-500/50 bg-yellow-500/5 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
    />
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

// Form Textarea Component with highlight support
const FormTextarea = ({ label, required, error, icon: Icon, className = "", highlight = false, ...props }) => (
  <div className={`space-y-2 ${className}`}>
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
      {highlight && (
        <span className="ml-auto flex items-center gap-1 text-yellow-400 text-xs">
          <AlertCircle className="w-3 h-3" />
          Missing
        </span>
      )}
    </label>
    <textarea
      {...props}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body resize-none ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : highlight
          ? "border-yellow-500/50 bg-yellow-500/5 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
    />
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

// Form Select Component with highlight support
const FormSelect = ({ label, required, error, icon: Icon, options, className = "", highlight = false, ...props }) => (
  <div className={`space-y-2 ${className}`}>
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
      {highlight && (
        <span className="ml-auto flex items-center gap-1 text-yellow-400 text-xs">
          <AlertCircle className="w-3 h-3" />
          Missing
        </span>
      )}
    </label>
    <select
      {...props}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] focus:outline-none transition-all font-body appearance-none cursor-pointer ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : highlight
          ? "border-yellow-500/50 bg-yellow-500/5 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
        backgroundPosition: "right 0.75rem center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "1.5em 1.5em",
      }}
    >
      <option value="" className="bg-[#0F1115]">
        Select...
      </option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} className="bg-[#0F1115]">
          {opt.label}
        </option>
      ))}
    </select>
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

// Toggle Switch Component
const ToggleSwitch = ({ label, checked, onChange, icon: Icon }) => (
  <label className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:border-white/20 transition-all">
    <div className="flex items-center gap-3">
      {Icon && <Icon className="w-5 h-5 text-[#00D1FF]" />}
      <span className="text-[#F3F4F6] text-sm font-body">{label}</span>
    </div>
    <div
      className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
        checked ? "bg-[#00D1FF]" : "bg-white/20"
      }`}
      onClick={() => onChange(!checked)}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </div>
  </label>
);

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, hasEmptyFields = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`border rounded-xl overflow-hidden ${hasEmptyFields ? "border-yellow-500/30" : "border-white/10"}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className={`w-5 h-5 ${hasEmptyFields ? "text-yellow-400" : "text-[#00D1FF]"}`} />}
          <span className="font-heading font-semibold text-[#F3F4F6]">{title}</span>
          {hasEmptyFields && (
            <span className="text-yellow-400 text-xs flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Has missing fields
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#9CA3AF]" />
        ) : (
          <ChevronDown className="w-5 h-5 text-[#9CA3AF]" />
        )}
      </button>
      {isOpen && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
};

// Auto-Fill Mode Component
const AutoFillMode = ({ onAnalysisComplete, isAnalyzing, setIsAnalyzing }) => {
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  const handleAnalyze = async () => {
    if (!description.trim()) return;
    
    setIsAnalyzing(true);
    setError("");
    
    try {
      const token = getClientAuthToken();
    if (!token) {
        setError("Please log in to use this feature");
        setIsAnalyzing(false);
        return;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/listings/convert-text-to-json`,
        { description: description.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data.status === "ok" && response.data.data) {
        // Pass the extracted data to parent and switch to manual tab
        onAnalysisComplete(response.data.data);
      } else {
        setError("Failed to analyze the description. Please try again.");
      }
    } catch (err) {
      console.error("Error analyzing description:", err);
      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
      } else if (err.response?.status === 403) {
        setError("Please log in to use this feature.");
      } else {
        setError(parseApiError(err));
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#00D1FF]/10 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-[#00D1FF]" />
        </div>
        <h3 className="font-heading font-bold text-xl text-[#F3F4F6] mb-2">
          AI-Powered Auto-Fill
        </h3>
        <p className="text-[#9CA3AF] text-sm font-body max-w-md mx-auto">
          Paste your property description and let AI extract all the relevant details automatically.
        </p>
      </div>

      <FormTextarea
        label="Property Description"
        required
        placeholder="Paste your full property description here. Include details about location, features, bedrooms, bathrooms, pricing, and any other relevant information..."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        icon={FileText}
        rows={10}
      />

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <button
        onClick={handleAnalyze}
        disabled={!description.trim() || isAnalyzing}
        className={`w-full flex items-center justify-center gap-2 py-4 rounded-lg font-heading font-bold transition-all ${
          description.trim() && !isAnalyzing
            ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
            : "bg-[#00D1FF]/30 text-[#0F1115]/50 cursor-not-allowed"
        }`}
      >
        {isAnalyzing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Analyzing...
          </>
        ) : (
          <>
            <Search className="w-5 h-5" />
            Analyze & Auto-Fill
          </>
        )}
      </button>
    </div>
  );
};

// Initial empty form data
const getInitialFormData = () => ({
  status: "active",
  transaction_type: "buy",
  description: "",
  basic_info: {
    address: "",
    suburb: "",
    postcode: "",
    city: "",
    state: "NSW",
    country: "Australia",
    property_type: "",
    headline: "",
    year_built: "",
    aspect: "",
    coordinates: { lat: "", lng: "" },
  },
  specifications: {
    bedrooms: "",
    bathrooms: "",
    ensuites: "",
    toilets: "",
    car_spaces: "",
    car_parking_type: "",
    land_size: "",
    land_unit: "sqm",
    floor_area: "",
    living_areas: "",
  },
  features: {
    climate_control: [],
    energy_efficiency: [],
    outdoor: [],
    indoor: [],
    is_pet_friendly: false,
  },
  pricing_and_financials: {
    display_price: "",
    search_price: "",
    is_negotiable: false,
    council_rates: "",
    water_rates: "",
    strata_fees: "",
    zoning: "",
  },
  location_context: {
    school_catchment: [],
    proximity: {
      transport: "",
      beach: "",
      shopping: "",
      hospital: "",
      parks: "",
    },
    walk_score: "",
  },
  ai_knowledge_base: {
    description: "",
    key_selling_points: [],
    inspection_notes: "",
  },
  media: {
    primary_image: "",
    images: [],
    virtual_tour_url: "",
    floor_plan_url: "",
    video_url: "",
  },
});

// Manual Mode Component
const ManualMode = ({ formData, setFormData, highlightEmpty, onSave, isSaving }) => {
  const updateBasicInfo = (field, value) => {
    setFormData({
      ...formData,
      basic_info: { ...formData.basic_info, [field]: value },
    });
  };

  const updateSpecifications = (field, value) => {
    setFormData({
      ...formData,
      specifications: { ...formData.specifications, [field]: value },
    });
  };

  const updateFeatures = (field, value) => {
    setFormData({
      ...formData,
      features: { ...formData.features, [field]: value },
    });
  };

  const updatePricing = (field, value) => {
    setFormData({
      ...formData,
      pricing_and_financials: { ...formData.pricing_and_financials, [field]: value },
    });
  };

  const updateLocationContext = (field, value) => {
    setFormData({
      ...formData,
      location_context: { ...formData.location_context, [field]: value },
    });
  };

  const updateProximity = (field, value) => {
    setFormData({
      ...formData,
      location_context: {
        ...formData.location_context,
        proximity: { ...formData.location_context.proximity, [field]: value },
      },
    });
  };

  const updateAIKnowledgeBase = (field, value) => {
    setFormData({
      ...formData,
      ai_knowledge_base: { ...formData.ai_knowledge_base, [field]: value },
    });
  };

  const updateMedia = (field, value) => {
    setFormData({
      ...formData,
      media: { ...formData.media, [field]: value },
    });
  };

  const propertyTypes = [
    { value: "House", label: "House" },
    { value: "Apartment", label: "Apartment" },
    { value: "Townhouse", label: "Townhouse" },
    { value: "Unit", label: "Unit" },
    { value: "Villa", label: "Villa" },
    { value: "Land", label: "Land" },
    { value: "Terrace", label: "Terrace" },
    { value: "Duplex", label: "Duplex" },
    { value: "Studio", label: "Studio" },
  ];

  const states = [
    { value: "NSW", label: "New South Wales" },
    { value: "VIC", label: "Victoria" },
    { value: "QLD", label: "Queensland" },
    { value: "WA", label: "Western Australia" },
    { value: "SA", label: "South Australia" },
    { value: "TAS", label: "Tasmania" },
    { value: "ACT", label: "Australian Capital Territory" },
    { value: "NT", label: "Northern Territory" },
  ];

  const aspects = [
    { value: "North-facing", label: "North-facing" },
    { value: "South-facing", label: "South-facing" },
    { value: "East-facing", label: "East-facing" },
    { value: "West-facing", label: "West-facing" },
    { value: "North-East", label: "North-East" },
    { value: "North-West", label: "North-West" },
    { value: "South-East", label: "South-East" },
    { value: "South-West", label: "South-West" },
  ];

  const parkingTypes = [
    { value: "Garage", label: "Garage" },
    { value: "Double Garage", label: "Double Garage" },
    { value: "Triple Garage", label: "Triple Garage" },
    { value: "Carport", label: "Carport" },
    { value: "Off-street", label: "Off-street" },
    { value: "On-street", label: "On-street" },
    { value: "Basement", label: "Basement Parking" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "under-offer", label: "Under Offer" },
    { value: "sold", label: "Sold" },
    { value: "withdrawn", label: "Withdrawn" },
    { value: "off-market", label: "Off Market" },
  ];

  // Check if sections have empty fields (for highlighting)
  const basicInfoHasEmpty = highlightEmpty && (
    isEmpty(formData.basic_info.headline) ||
    isEmpty(formData.basic_info.address) ||
    isEmpty(formData.basic_info.suburb) ||
    isEmpty(formData.basic_info.postcode) ||
    isEmpty(formData.basic_info.city) ||
    isEmpty(formData.basic_info.property_type)
  );

  const specificationsHasEmpty = highlightEmpty && (
    isEmpty(formData.specifications.bedrooms) ||
    isEmpty(formData.specifications.bathrooms)
  );

  const pricingHasEmpty = highlightEmpty && isEmpty(formData.pricing_and_financials.display_price);

  return (
    <div className="space-y-6">
      {/* Success message when data is loaded */}
      {highlightEmpty && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-green-400 font-semibold text-sm">AI Analysis Complete!</p>
            <p className="text-green-400/80 text-sm mt-1">
              Form has been populated with extracted data. Fields highlighted in yellow are missing and may need manual input.
            </p>
          </div>
        </div>
      )}

      {/* Status & Description */}
      <CollapsibleSection title="Status & Description" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            label="Listing Status"
            required
            options={statusOptions}
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            icon={Settings2}
          />
          <FormSelect
            label="Transaction Type"
            required
            options={[
              { value: "buy", label: "Buy (For Sale)" },
              { value: "rent", label: "Rent (For Lease)" },
            ]}
            value={formData.transaction_type || "buy"}
            onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
            icon={DollarSign}
          />
        </div>
        <FormTextarea
          label="General Description"
          placeholder="Brief description of the property..."
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
        />
      </CollapsibleSection>

      {/* Basic Info */}
      <CollapsibleSection title="Basic Information" icon={Home} hasEmptyFields={basicInfoHasEmpty}>
        <FormInput
          label="Headline"
          required
          placeholder="e.g., Stunning Family Home with Ocean Views"
          value={formData.basic_info.headline || ""}
          onChange={(e) => updateBasicInfo("headline", e.target.value)}
          highlight={highlightEmpty && isEmpty(formData.basic_info.headline)}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            label="Property Type"
            required
            options={propertyTypes}
            value={formData.basic_info.property_type || ""}
            onChange={(e) => updateBasicInfo("property_type", e.target.value)}
            icon={Building2}
            highlight={highlightEmpty && isEmpty(formData.basic_info.property_type)}
          />
          <FormInput
            label="Year Built"
            type="number"
            placeholder="e.g., 1995"
            value={formData.basic_info.year_built || ""}
            onChange={(e) => updateBasicInfo("year_built", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.basic_info.year_built)}
          />
        </div>
        <FormInput
          label="Street Address"
          required
          placeholder="e.g., 123 Bondi Rd"
          value={formData.basic_info.address || ""}
          onChange={(e) => updateBasicInfo("address", e.target.value)}
          icon={MapPin}
          highlight={highlightEmpty && isEmpty(formData.basic_info.address)}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FormInput
            label="Suburb"
            required
            placeholder="Bondi"
            value={formData.basic_info.suburb || ""}
            onChange={(e) => updateBasicInfo("suburb", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.basic_info.suburb)}
          />
          <FormInput
            label="Postcode"
            required
            placeholder="2026"
            value={formData.basic_info.postcode || ""}
            onChange={(e) => updateBasicInfo("postcode", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.basic_info.postcode)}
          />
          <FormInput
            label="City"
            required
            placeholder="Sydney"
            value={formData.basic_info.city || ""}
            onChange={(e) => updateBasicInfo("city", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.basic_info.city)}
          />
          <FormSelect
            label="State"
            required
            options={states}
            value={formData.basic_info.state || "NSW"}
            onChange={(e) => updateBasicInfo("state", e.target.value)}
          />
        </div>
        <FormSelect
          label="Aspect"
          options={aspects}
          value={formData.basic_info.aspect || ""}
          onChange={(e) => updateBasicInfo("aspect", e.target.value)}
          icon={Navigation}
          className="md:w-1/2"
          highlight={highlightEmpty && isEmpty(formData.basic_info.aspect)}
        />
      </CollapsibleSection>

      {/* Specifications */}
      <CollapsibleSection title="Specifications" icon={Ruler} hasEmptyFields={specificationsHasEmpty}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FormInput
            label="Bedrooms"
            required
            type="number"
            placeholder="0"
            value={formData.specifications.bedrooms ?? ""}
            onChange={(e) => updateSpecifications("bedrooms", e.target.value)}
            icon={Bed}
            highlight={highlightEmpty && isEmpty(formData.specifications.bedrooms)}
          />
          <FormInput
            label="Bathrooms"
            required
            type="number"
            placeholder="0"
            value={formData.specifications.bathrooms ?? ""}
            onChange={(e) => updateSpecifications("bathrooms", e.target.value)}
            icon={Bath}
            highlight={highlightEmpty && isEmpty(formData.specifications.bathrooms)}
          />
          <FormInput
            label="Ensuites"
            type="number"
            placeholder="0"
            value={formData.specifications.ensuites ?? ""}
            onChange={(e) => updateSpecifications("ensuites", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.specifications.ensuites)}
          />
          <FormInput
            label="Toilets"
            type="number"
            placeholder="0"
            value={formData.specifications.toilets ?? ""}
            onChange={(e) => updateSpecifications("toilets", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.specifications.toilets)}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <FormInput
            label="Car Spaces"
            type="number"
            placeholder="0"
            value={formData.specifications.car_spaces ?? ""}
            onChange={(e) => updateSpecifications("car_spaces", e.target.value)}
            icon={Car}
            highlight={highlightEmpty && isEmpty(formData.specifications.car_spaces)}
          />
          <FormSelect
            label="Parking Type"
            options={parkingTypes}
            value={formData.specifications.car_parking_type || ""}
            onChange={(e) => updateSpecifications("car_parking_type", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.specifications.car_parking_type)}
          />
          <FormInput
            label="Living Areas"
            type="number"
            placeholder="1"
            value={formData.specifications.living_areas ?? ""}
            onChange={(e) => updateSpecifications("living_areas", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.specifications.living_areas)}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <FormInput
            label="Land Size (sqm)"
            type="number"
            placeholder="0"
            value={formData.specifications.land_size ?? ""}
            onChange={(e) => updateSpecifications("land_size", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.specifications.land_size)}
          />
          <FormInput
            label="Floor Area (sqm)"
            type="number"
            placeholder="0"
            value={formData.specifications.floor_area ?? ""}
            onChange={(e) => updateSpecifications("floor_area", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.specifications.floor_area)}
          />
        </div>
      </CollapsibleSection>

      {/* Features */}
      <CollapsibleSection title="Features & Amenities" icon={Zap} defaultOpen={false}>
        <FormInput
          label="Climate Control"
          placeholder="e.g., Ducted Air Conditioning, Fireplace (comma separated)"
          value={(formData.features.climate_control || []).join(", ")}
          onChange={(e) =>
            updateFeatures(
              "climate_control",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
          highlight={highlightEmpty && isEmpty(formData.features.climate_control)}
        />
        <FormInput
          label="Energy Efficiency"
          placeholder="e.g., Solar Panels 5kW, Water Tank (comma separated)"
          value={(formData.features.energy_efficiency || []).join(", ")}
          onChange={(e) =>
            updateFeatures(
              "energy_efficiency",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
          icon={Zap}
          highlight={highlightEmpty && isEmpty(formData.features.energy_efficiency)}
        />
        <FormInput
          label="Outdoor Features"
          placeholder="e.g., In-ground Pool, Deck, Fully Fenced (comma separated)"
          value={(formData.features.outdoor || []).join(", ")}
          onChange={(e) =>
            updateFeatures(
              "outdoor",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
          icon={Trees}
          highlight={highlightEmpty && isEmpty(formData.features.outdoor)}
        />
        <FormInput
          label="Indoor Features"
          placeholder="e.g., Built-in Wardrobes, Dishwasher, Floorboards (comma separated)"
          value={(formData.features.indoor || []).join(", ")}
          onChange={(e) =>
            updateFeatures(
              "indoor",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
          highlight={highlightEmpty && isEmpty(formData.features.indoor)}
        />
        <ToggleSwitch
          label="Pet Friendly"
          checked={formData.features.is_pet_friendly || false}
          onChange={(checked) => updateFeatures("is_pet_friendly", checked)}
          icon={Dog}
        />
      </CollapsibleSection>

      {/* Pricing & Financials */}
      <CollapsibleSection title="Pricing & Financials" icon={DollarSign} defaultOpen={false} hasEmptyFields={pricingHasEmpty}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Display Price"
            required
            placeholder="e.g., $1,850,000 or Contact Agent"
            value={formData.pricing_and_financials.display_price || ""}
            onChange={(e) => updatePricing("display_price", e.target.value)}
            icon={DollarSign}
            highlight={highlightEmpty && isEmpty(formData.pricing_and_financials.display_price)}
          />
          <FormInput
            label="Search Price (numeric)"
            type="number"
            placeholder="e.g., 1850000"
            value={formData.pricing_and_financials.search_price ?? ""}
            onChange={(e) => updatePricing("search_price", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.pricing_and_financials.search_price)}
          />
        </div>
        <ToggleSwitch
          label="Price is Negotiable"
          checked={formData.pricing_and_financials.is_negotiable || false}
          onChange={(checked) => updatePricing("is_negotiable", checked)}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FormInput
            label="Council Rates ($/year)"
            type="number"
            placeholder="0"
            value={formData.pricing_and_financials.council_rates ?? ""}
            onChange={(e) => updatePricing("council_rates", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.pricing_and_financials.council_rates)}
          />
          <FormInput
            label="Water Rates ($/year)"
            type="number"
            placeholder="0"
            value={formData.pricing_and_financials.water_rates ?? ""}
            onChange={(e) => updatePricing("water_rates", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.pricing_and_financials.water_rates)}
          />
          <FormInput
            label="Strata Fees ($/quarter)"
            type="number"
            placeholder="0"
            value={formData.pricing_and_financials.strata_fees ?? ""}
            onChange={(e) => updatePricing("strata_fees", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.pricing_and_financials.strata_fees)}
          />
          <FormInput
            label="Zoning"
            placeholder="e.g., R2"
            value={formData.pricing_and_financials.zoning || ""}
            onChange={(e) => updatePricing("zoning", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.pricing_and_financials.zoning)}
          />
        </div>
      </CollapsibleSection>

      {/* Location Context */}
      <CollapsibleSection title="Location & Proximity" icon={MapPin} defaultOpen={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Nearest Transport"
            placeholder="e.g., 400m to Bondi Junction Station"
            value={formData.location_context.proximity?.transport || ""}
            onChange={(e) => updateProximity("transport", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.location_context.proximity?.transport)}
          />
          <FormInput
            label="Nearest Beach"
            placeholder="e.g., 1.2km to Bondi Beach"
            value={formData.location_context.proximity?.beach || ""}
            onChange={(e) => updateProximity("beach", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.location_context.proximity?.beach)}
          />
          <FormInput
            label="Nearest Shopping"
            placeholder="e.g., 600m to Westfield"
            value={formData.location_context.proximity?.shopping || ""}
            onChange={(e) => updateProximity("shopping", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.location_context.proximity?.shopping)}
          />
          <FormInput
            label="Nearest Hospital"
            placeholder="e.g., 3km to St Vincent's"
            value={formData.location_context.proximity?.hospital || ""}
            onChange={(e) => updateProximity("hospital", e.target.value)}
            highlight={highlightEmpty && isEmpty(formData.location_context.proximity?.hospital)}
          />
        </div>
        <FormInput
          label="Walk Score"
          type="number"
          placeholder="0-100"
          value={formData.location_context.walk_score ?? ""}
          onChange={(e) => updateLocationContext("walk_score", e.target.value)}
          className="md:w-1/4"
          highlight={highlightEmpty && isEmpty(formData.location_context.walk_score)}
        />
      </CollapsibleSection>

      {/* AI Knowledge Base */}
      <CollapsibleSection title="AI Knowledge Base" icon={Brain} defaultOpen={false}>
        <FormTextarea
          label="Full Description (for AI/RAG)"
          placeholder="Detailed marketing description for AI to use..."
          value={formData.ai_knowledge_base.description || ""}
          onChange={(e) => updateAIKnowledgeBase("description", e.target.value)}
          rows={4}
          highlight={highlightEmpty && isEmpty(formData.ai_knowledge_base.description)}
        />
        <FormInput
          label="Key Selling Points"
          placeholder="e.g., Ocean views, Quiet cul-de-sac (comma separated)"
          value={(formData.ai_knowledge_base.key_selling_points || []).join(", ")}
          onChange={(e) =>
            updateAIKnowledgeBase(
              "key_selling_points",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
          highlight={highlightEmpty && isEmpty(formData.ai_knowledge_base.key_selling_points)}
        />
        <FormTextarea
          label="Inspection Notes (Private)"
          placeholder="Private notes for inspections..."
          value={formData.ai_knowledge_base.inspection_notes || ""}
          onChange={(e) => updateAIKnowledgeBase("inspection_notes", e.target.value)}
          rows={2}
        />
      </CollapsibleSection>

      {/* Media */}
      <CollapsibleSection title="Media & Images" icon={ImageIcon} defaultOpen={false}>
        <FormInput
          label="Primary Image URL"
          placeholder="https://..."
          value={formData.media.primary_image || ""}
          onChange={(e) => updateMedia("primary_image", e.target.value)}
          icon={ImageIcon}
          highlight={highlightEmpty && isEmpty(formData.media.primary_image)}
        />
        <FormInput
          label="Floor Plan URL"
          placeholder="https://..."
          value={formData.media.floor_plan_url || ""}
          onChange={(e) => updateMedia("floor_plan_url", e.target.value)}
          highlight={highlightEmpty && isEmpty(formData.media.floor_plan_url)}
        />
        <FormInput
          label="Virtual Tour URL"
          placeholder="https://..."
          value={formData.media.virtual_tour_url || ""}
          onChange={(e) => updateMedia("virtual_tour_url", e.target.value)}
          highlight={highlightEmpty && isEmpty(formData.media.virtual_tour_url)}
        />
        <FormInput
          label="Video URL"
          placeholder="https://..."
          value={formData.media.video_url || ""}
          onChange={(e) => updateMedia("video_url", e.target.value)}
          icon={Video}
          highlight={highlightEmpty && isEmpty(formData.media.video_url)}
        />
      </CollapsibleSection>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          className={`w-full flex items-center justify-center gap-2 py-4 rounded-lg font-heading font-bold transition-all ${
            isSaving
              ? "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
              : "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
          }`}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Plus className="w-5 h-5" />
              Create Listing
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Add Listing Modal Component
const AddListingModal = ({ isOpen, onClose, onListingSaved }) => {
  const [activeTab, setActiveTab] = useState("auto-fill");
  const [formData, setFormData] = useState(getInitialFormData());
  const [highlightEmpty, setHighlightEmpty] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Handle saving the listing
  const handleSaveListing = async () => {
    setIsSaving(true);
    setSaveError("");

    try {
      const token = getClientAuthToken();
    if (!token) {
        setSaveError("Please log in to save listings");
        setIsSaving(false);
        return;
      }

      // Helper to clean empty strings to null
      const cleanValue = (val) => (val === "" || val === undefined ? null : val);
      const cleanInt = (val) => {
        const parsed = parseInt(val);
        return isNaN(parsed) ? 0 : parsed;
      };
      const cleanFloat = (val) => {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? null : parsed;
      };

      // Prepare the listing data for the API - all fields optional
      const listingPayload = {
        status: formData.status || "active",
        transaction_type: formData.transaction_type || "buy",
        description: formData.description || "",
        basic_info: {
          address: cleanValue(formData.basic_info?.address),
          suburb: cleanValue(formData.basic_info?.suburb),
          postcode: cleanValue(formData.basic_info?.postcode),
          city: cleanValue(formData.basic_info?.city),
          state: cleanValue(formData.basic_info?.state) || "NSW",
          country: formData.basic_info?.country || "Australia",
          property_type: cleanValue(formData.basic_info?.property_type),
          headline: cleanValue(formData.basic_info?.headline),
          year_built: cleanInt(formData.basic_info?.year_built) || null,
          aspect: cleanValue(formData.basic_info?.aspect),
          coordinates: formData.basic_info?.coordinates?.lat && formData.basic_info?.coordinates?.lng 
            ? { lat: parseFloat(formData.basic_info.coordinates.lat), lng: parseFloat(formData.basic_info.coordinates.lng) }
            : null,
        },
        specifications: {
          bedrooms: cleanInt(formData.specifications?.bedrooms),
          bathrooms: cleanInt(formData.specifications?.bathrooms),
          ensuites: cleanInt(formData.specifications?.ensuites),
          toilets: cleanInt(formData.specifications?.toilets),
          car_spaces: cleanInt(formData.specifications?.car_spaces),
          car_parking_type: cleanValue(formData.specifications?.car_parking_type),
          land_size: cleanFloat(formData.specifications?.land_size),
          land_unit: formData.specifications?.land_unit || "sqm",
          floor_area: cleanFloat(formData.specifications?.floor_area),
          living_areas: cleanInt(formData.specifications?.living_areas) || 1,
        },
        features: {
          climate_control: formData.features?.climate_control || [],
          energy_efficiency: formData.features?.energy_efficiency || [],
          outdoor: formData.features?.outdoor || [],
          indoor: formData.features?.indoor || [],
          is_pet_friendly: formData.features?.is_pet_friendly || false,
        },
        pricing_and_financials: {
          display_price: cleanValue(formData.pricing_and_financials?.display_price),
          search_price: cleanFloat(formData.pricing_and_financials?.search_price),
          is_negotiable: formData.pricing_and_financials?.is_negotiable || false,
          council_rates: cleanFloat(formData.pricing_and_financials?.council_rates),
          water_rates: cleanFloat(formData.pricing_and_financials?.water_rates),
          strata_fees: cleanFloat(formData.pricing_and_financials?.strata_fees),
          zoning: cleanValue(formData.pricing_and_financials?.zoning),
        },
        location_context: {
          school_catchment: formData.location_context?.school_catchment || [],
          proximity: {
            transport: cleanValue(formData.location_context?.proximity?.transport),
            beach: cleanValue(formData.location_context?.proximity?.beach),
            shopping: cleanValue(formData.location_context?.proximity?.shopping),
            hospital: cleanValue(formData.location_context?.proximity?.hospital),
            parks: cleanValue(formData.location_context?.proximity?.parks),
          },
          walk_score: cleanInt(formData.location_context?.walk_score) || null,
        },
        ai_knowledge_base: {
          description: formData.ai_knowledge_base?.description || "",
          key_selling_points: formData.ai_knowledge_base?.key_selling_points || [],
          inspection_notes: formData.ai_knowledge_base?.inspection_notes || "",
        },
        media: {
          primary_image: cleanValue(formData.media?.primary_image),
          images: formData.media?.images || [],
          virtual_tour_url: cleanValue(formData.media?.virtual_tour_url),
          floor_plan_url: cleanValue(formData.media?.floor_plan_url),
          video_url: cleanValue(formData.media?.video_url),
        },
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/listings/save_listings`,
        listingPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "ok") {
        // Success! Close modal and notify parent
        if (onListingSaved) {
          onListingSaved(response.data.listing_id);
        }
        handleClose();
      } else {
        setSaveError("Failed to save listing. Please try again.");
      }
    } catch (err) {
      console.error("Error saving listing:", err);
      if (err.response?.status === 401) {
        setSaveError("Session expired. Please log in again.");
      } else if (err.response?.status === 403) {
        setSaveError("Please log in to save listings.");
      } else {
        setSaveError(parseApiError(err));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle analysis complete - populate form and switch to manual tab
  const handleAnalysisComplete = (data) => {
    // Merge API response with form data, preserving structure
    const mergedData = {
      ...getInitialFormData(),
      status: data.status || "active",
      transaction_type: data.transaction_type || formData?.transaction_type || "buy",
      description: data.description || "",
      basic_info: {
        ...getInitialFormData().basic_info,
        ...data.basic_info,
        coordinates: data.basic_info?.coordinates || { lat: "", lng: "" },
      },
      specifications: {
        ...getInitialFormData().specifications,
        ...data.specifications,
      },
      features: {
        ...getInitialFormData().features,
        ...data.features,
        climate_control: data.features?.climate_control || [],
        energy_efficiency: data.features?.energy_efficiency || [],
        outdoor: data.features?.outdoor || [],
        indoor: data.features?.indoor || [],
      },
      pricing_and_financials: {
        ...getInitialFormData().pricing_and_financials,
        ...data.pricing_and_financials,
      },
      location_context: {
        ...getInitialFormData().location_context,
        ...data.location_context,
        proximity: {
          ...getInitialFormData().location_context.proximity,
          ...data.location_context?.proximity,
        },
        school_catchment: data.location_context?.school_catchment || [],
      },
      ai_knowledge_base: {
        ...getInitialFormData().ai_knowledge_base,
        ...data.ai_knowledge_base,
        key_selling_points: data.ai_knowledge_base?.key_selling_points || [],
      },
      media: {
        ...getInitialFormData().media,
        ...data.media,
        images: data.media?.images || [],
      },
    };

    setFormData(mergedData);
    setHighlightEmpty(true);
    setActiveTab("manual");
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData(getInitialFormData());
    setHighlightEmpty(false);
    setActiveTab("auto-fill");
    setIsAnalyzing(false);
    setIsSaving(false);
    setSaveError("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0F1115] border-white/10 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-white/10 pb-4">
          <DialogTitle className="font-heading font-bold text-2xl text-[#F3F4F6] flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00D1FF]/10 flex items-center justify-center">
              <Plus className="w-5 h-5 text-[#00D1FF]" />
            </div>
            Add New Property Listing
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2 bg-white/5 p-1 rounded-lg">
            <TabsTrigger
              value="auto-fill"
              disabled={isAnalyzing}
              className="flex items-center gap-2 data-[state=active]:bg-[#00D1FF] data-[state=active]:text-[#0F1115] rounded-md py-2.5 font-heading font-semibold transition-all disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              Auto-Fill
            </TabsTrigger>
            <TabsTrigger
              value="manual"
              disabled={isAnalyzing}
              className="flex items-center gap-2 data-[state=active]:bg-[#00D1FF] data-[state=active]:text-[#0F1115] rounded-md py-2.5 font-heading font-semibold transition-all disabled:opacity-50"
            >
              <PenLine className="w-4 h-4" />
              Manual
              {highlightEmpty && (
                <span className="ml-1 w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              )}
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4 pr-2">
            <TabsContent value="auto-fill" className="mt-0">
              <AutoFillMode 
                onAnalysisComplete={handleAnalysisComplete}
                isAnalyzing={isAnalyzing}
                setIsAnalyzing={setIsAnalyzing}
              />
            </TabsContent>
            <TabsContent value="manual" className="mt-0">
              {saveError && (
                <div className="flex items-center gap-2 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 mb-4">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{saveError}</p>
                </div>
              )}
              <ManualMode 
                formData={formData}
                setFormData={setFormData}
                highlightEmpty={highlightEmpty}
                onSave={handleSaveListing}
                isSaving={isSaving}
              />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// View Listing Modal Component
const ViewListingModal = ({ isOpen, onClose, listing, onEdit }) => {
  if (!listing) return null;

  const basicInfo = listing.basic_info || {};
  const specifications = listing.specifications || {};
  const features = listing.features || {};
  const pricingAndFinancials = listing.pricing_and_financials || {};
  const locationContext = listing.location_context || {};
  const aiKnowledgeBase = listing.ai_knowledge_base || {};
  const media = listing.media || {};
  const proximity = locationContext.proximity || {};

  // Helper to render list items
  const renderList = (items) => {
    if (!items || items.length === 0) return <span className="text-[#9CA3AF]/50 italic">Not specified</span>;
    return items.join(", ");
  };

  // Helper to render value or placeholder
  const renderValue = (value, placeholder = "Not specified") => {
    if (value === null || value === undefined || value === "") {
      return <span className="text-[#9CA3AF]/50 italic">{placeholder}</span>;
    }
    return value;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F1115] border-white/10 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-white/10 pb-4">
          <DialogTitle className="font-heading font-bold text-2xl text-[#F3F4F6] flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00D1FF]/10 flex items-center justify-center">
              <Eye className="w-5 h-5 text-[#00D1FF]" />
            </div>
            View Listing Details
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto mt-4 pr-2 space-y-6">
          {/* Hero Section */}
          <div className="relative rounded-xl overflow-hidden">
            {media.primary_image ? (
              <img
                src={media.primary_image}
                alt={basicInfo.headline || "Property"}
                className="w-full h-64 object-cover"
              />
            ) : (
              <div className="w-full h-64 bg-white/5 flex items-center justify-center">
                <Home className="w-16 h-16 text-[#9CA3AF]/30" />
              </div>
            )}
            <div className="absolute top-4 left-4">
              <StatusBadge status={listing.status} />
            </div>
          </div>

          {/* Title & Address */}
          <div>
            <h2 className="font-heading font-bold text-2xl text-[#F3F4F6] mb-2">
              {basicInfo.headline || "Untitled Listing"}
            </h2>
            <div className="flex items-center gap-2 text-[#9CA3AF]">
              <MapPin className="w-5 h-5 text-[#00D1FF]" />
              <span>
                {basicInfo.address ? `${basicInfo.address}, ` : ""}
                {basicInfo.suburb || ""} {basicInfo.state || ""} {basicInfo.postcode || ""}
              </span>
            </div>
          </div>

          {/* Key Specs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass rounded-lg p-4 text-center">
              <Bed className="w-6 h-6 text-[#00D1FF] mx-auto mb-2" />
              <div className="text-2xl font-bold text-[#F3F4F6]">{specifications.bedrooms ?? 0}</div>
              <div className="text-xs text-[#9CA3AF]">Bedrooms</div>
            </div>
            <div className="glass rounded-lg p-4 text-center">
              <Bath className="w-6 h-6 text-[#00D1FF] mx-auto mb-2" />
              <div className="text-2xl font-bold text-[#F3F4F6]">{specifications.bathrooms ?? 0}</div>
              <div className="text-xs text-[#9CA3AF]">Bathrooms</div>
            </div>
            <div className="glass rounded-lg p-4 text-center">
              <Car className="w-6 h-6 text-[#00D1FF] mx-auto mb-2" />
              <div className="text-2xl font-bold text-[#F3F4F6]">{specifications.car_spaces ?? 0}</div>
              <div className="text-xs text-[#9CA3AF]">Car Spaces</div>
            </div>
            <div className="glass rounded-lg p-4 text-center">
              <DollarSign className="w-6 h-6 text-[#00D1FF] mx-auto mb-2" />
              <div className="text-xl font-bold text-[#00D1FF]">{pricingAndFinancials.display_price || "Contact"}</div>
              <div className="text-xs text-[#9CA3AF]">Price</div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="glass rounded-xl p-4 border border-white/10">
            <h3 className="font-heading font-semibold text-lg text-[#F3F4F6] mb-4 flex items-center gap-2">
              <Home className="w-5 h-5 text-[#00D1FF]" />
              Basic Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-[#9CA3AF]">Property Type:</span>
                <div className="text-[#F3F4F6]">{renderValue(basicInfo.property_type)}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Year Built:</span>
                <div className="text-[#F3F4F6]">{renderValue(basicInfo.year_built)}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Aspect:</span>
                <div className="text-[#F3F4F6]">{renderValue(basicInfo.aspect)}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Country:</span>
                <div className="text-[#F3F4F6]">{renderValue(basicInfo.country)}</div>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="glass rounded-xl p-4 border border-white/10">
            <h3 className="font-heading font-semibold text-lg text-[#F3F4F6] mb-4 flex items-center gap-2">
              <Ruler className="w-5 h-5 text-[#00D1FF]" />
              Specifications
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-[#9CA3AF]">Ensuites:</span>
                <div className="text-[#F3F4F6]">{specifications.ensuites ?? 0}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Toilets:</span>
                <div className="text-[#F3F4F6]">{specifications.toilets ?? 0}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Living Areas:</span>
                <div className="text-[#F3F4F6]">{specifications.living_areas ?? 1}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Parking Type:</span>
                <div className="text-[#F3F4F6]">{renderValue(specifications.car_parking_type)}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Land Size:</span>
                <div className="text-[#F3F4F6]">
                  {specifications.land_size ? `${specifications.land_size} ${specifications.land_unit || 'sqm'}` : renderValue(null)}
                </div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Floor Area:</span>
                <div className="text-[#F3F4F6]">
                  {specifications.floor_area ? `${specifications.floor_area} sqm` : renderValue(null)}
                </div>
              </div>
            </div>
          </div>

          {/* Features */}
          {(features.climate_control?.length > 0 || features.energy_efficiency?.length > 0 || 
            features.outdoor?.length > 0 || features.indoor?.length > 0 || features.is_pet_friendly) && (
            <div className="glass rounded-xl p-4 border border-white/10">
              <h3 className="font-heading font-semibold text-lg text-[#F3F4F6] mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#00D1FF]" />
                Features & Amenities
              </h3>
              <div className="space-y-3 text-sm">
                {features.climate_control?.length > 0 && (
                  <div>
                    <span className="text-[#9CA3AF]">Climate Control:</span>
                    <div className="text-[#F3F4F6]">{renderList(features.climate_control)}</div>
                  </div>
                )}
                {features.energy_efficiency?.length > 0 && (
                  <div>
                    <span className="text-[#9CA3AF]">Energy Efficiency:</span>
                    <div className="text-[#F3F4F6]">{renderList(features.energy_efficiency)}</div>
                  </div>
                )}
                {features.outdoor?.length > 0 && (
                  <div>
                    <span className="text-[#9CA3AF]">Outdoor:</span>
                    <div className="text-[#F3F4F6]">{renderList(features.outdoor)}</div>
                  </div>
                )}
                {features.indoor?.length > 0 && (
                  <div>
                    <span className="text-[#9CA3AF]">Indoor:</span>
                    <div className="text-[#F3F4F6]">{renderList(features.indoor)}</div>
                  </div>
                )}
                {features.is_pet_friendly && (
                  <div className="flex items-center gap-2">
                    <Dog className="w-4 h-4 text-green-400" />
                    <span className="text-green-400">Pet Friendly</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pricing & Financials */}
          <div className="glass rounded-xl p-4 border border-white/10">
            <h3 className="font-heading font-semibold text-lg text-[#F3F4F6] mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-[#00D1FF]" />
              Pricing & Financials
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-[#9CA3AF]">Display Price:</span>
                <div className="text-[#00D1FF] font-semibold">{renderValue(pricingAndFinancials.display_price)}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Negotiable:</span>
                <div className="text-[#F3F4F6]">{pricingAndFinancials.is_negotiable ? "Yes" : "No"}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Zoning:</span>
                <div className="text-[#F3F4F6]">{renderValue(pricingAndFinancials.zoning)}</div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Council Rates:</span>
                <div className="text-[#F3F4F6]">
                  {pricingAndFinancials.council_rates ? `$${pricingAndFinancials.council_rates}/year` : renderValue(null)}
                </div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Water Rates:</span>
                <div className="text-[#F3F4F6]">
                  {pricingAndFinancials.water_rates ? `$${pricingAndFinancials.water_rates}/year` : renderValue(null)}
                </div>
              </div>
              <div>
                <span className="text-[#9CA3AF]">Strata Fees:</span>
                <div className="text-[#F3F4F6]">
                  {pricingAndFinancials.strata_fees ? `$${pricingAndFinancials.strata_fees}/quarter` : renderValue(null)}
                </div>
              </div>
            </div>
          </div>

          {/* Location & Proximity */}
          {(proximity.transport || proximity.beach || proximity.shopping || proximity.hospital || proximity.parks || locationContext.walk_score) && (
            <div className="glass rounded-xl p-4 border border-white/10">
              <h3 className="font-heading font-semibold text-lg text-[#F3F4F6] mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#00D1FF]" />
                Location & Proximity
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {proximity.transport && (
                  <div>
                    <span className="text-[#9CA3AF]">Transport:</span>
                    <div className="text-[#F3F4F6]">{proximity.transport}</div>
                  </div>
                )}
                {proximity.beach && (
                  <div>
                    <span className="text-[#9CA3AF]">Beach:</span>
                    <div className="text-[#F3F4F6]">{proximity.beach}</div>
                  </div>
                )}
                {proximity.shopping && (
                  <div>
                    <span className="text-[#9CA3AF]">Shopping:</span>
                    <div className="text-[#F3F4F6]">{proximity.shopping}</div>
                  </div>
                )}
                {proximity.hospital && (
                  <div>
                    <span className="text-[#9CA3AF]">Hospital:</span>
                    <div className="text-[#F3F4F6]">{proximity.hospital}</div>
                  </div>
                )}
                {locationContext.walk_score && (
                  <div>
                    <span className="text-[#9CA3AF]">Walk Score:</span>
                    <div className="text-[#F3F4F6]">{locationContext.walk_score}/100</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {(listing.description || aiKnowledgeBase.description) && (
            <div className="glass rounded-xl p-4 border border-white/10">
              <h3 className="font-heading font-semibold text-lg text-[#F3F4F6] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#00D1FF]" />
                Description
              </h3>
              <p className="text-[#9CA3AF] text-sm whitespace-pre-wrap">
                {listing.description || aiKnowledgeBase.description}
              </p>
              {aiKnowledgeBase.key_selling_points?.length > 0 && (
                <div className="mt-4">
                  <span className="text-[#9CA3AF] text-sm">Key Selling Points:</span>
                  <ul className="list-disc list-inside text-[#F3F4F6] text-sm mt-1">
                    {aiKnowledgeBase.key_selling_points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Media Links */}
          {(media.virtual_tour_url || media.floor_plan_url || media.video_url) && (
            <div className="glass rounded-xl p-4 border border-white/10">
              <h3 className="font-heading font-semibold text-lg text-[#F3F4F6] mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-[#00D1FF]" />
                Media
              </h3>
              <div className="flex flex-wrap gap-3">
                {media.virtual_tour_url && (
                  <a
                    href={media.virtual_tour_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#00D1FF] transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Virtual Tour
                  </a>
                )}
                {media.floor_plan_url && (
                  <a
                    href={media.floor_plan_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#00D1FF] transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Floor Plan
                  </a>
                )}
                {media.video_url && (
                  <a
                    href={media.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#00D1FF] transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Video
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          {(listing.created_at || listing.updated_at) && (
            <div className="flex items-center justify-between text-xs text-[#9CA3AF]/70 pt-2 border-t border-white/10">
              {listing.created_at && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Created: {new Date(listing.created_at).toLocaleDateString()}
                </div>
              )}
              {listing.updated_at && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Updated: {new Date(listing.updated_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#F3F4F6] font-heading font-semibold transition-colors"
          >
            Close
          </button>
          {onEdit && (
            <button
              onClick={() => {
                onClose();
                onEdit(listing);
              }}
              className="px-4 py-2 rounded-lg bg-[#00D1FF] text-[#0F1115] font-heading font-bold hover:bg-[#00D1FF]/90 transition-colors flex items-center gap-2"
            >
              <Edit3 className="w-4 h-4" />
              Edit Listing
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edit Listing Modal Component
const EditListingModal = ({ isOpen, onClose, listing, onListingUpdated }) => {
  const [formData, setFormData] = useState(getInitialFormData());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Load listing data when modal opens
  React.useEffect(() => {
    if (isOpen && listing) {
      setIsLoading(true);
      // Fetch fresh data from API
      const fetchListing = async () => {
        try {
          const token = getClientAuthToken();
    if (!token) {
            setSaveError("Please log in to edit listings");
            setIsLoading(false);
            return;
          }

          const response = await axios.get(
            `${BACKEND_URL}/api/listings/${listing.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data.status === "ok" && response.data.listing) {
            const data = response.data.listing;
            // Populate form with fetched data
            setFormData({
              status: data.status || "active",
              transaction_type: data.transaction_type || "buy",
              description: data.description || "",
              basic_info: {
                ...getInitialFormData().basic_info,
                ...data.basic_info,
                coordinates: data.basic_info?.coordinates || { lat: "", lng: "" },
              },
              specifications: {
                ...getInitialFormData().specifications,
                ...data.specifications,
              },
              features: {
                ...getInitialFormData().features,
                ...data.features,
                climate_control: data.features?.climate_control || [],
                energy_efficiency: data.features?.energy_efficiency || [],
                outdoor: data.features?.outdoor || [],
                indoor: data.features?.indoor || [],
              },
              pricing_and_financials: {
                ...getInitialFormData().pricing_and_financials,
                ...data.pricing_and_financials,
              },
              location_context: {
                ...getInitialFormData().location_context,
                ...data.location_context,
                proximity: {
                  ...getInitialFormData().location_context.proximity,
                  ...data.location_context?.proximity,
                },
                school_catchment: data.location_context?.school_catchment || [],
              },
              ai_knowledge_base: {
                ...getInitialFormData().ai_knowledge_base,
                ...data.ai_knowledge_base,
                key_selling_points: data.ai_knowledge_base?.key_selling_points || [],
              },
              media: {
                ...getInitialFormData().media,
                ...data.media,
                images: data.media?.images || [],
              },
            });
          }
        } catch (err) {
          console.error("Error fetching listing:", err);
          setSaveError("Failed to load listing data");
        } finally {
          setIsLoading(false);
        }
      };

      fetchListing();
    }
  }, [isOpen, listing]);

  // Handle update
  const handleUpdateListing = async () => {
    if (!listing?.id) return;

    setIsSaving(true);
    setSaveError("");

    try {
      const token = getClientAuthToken();
    if (!token) {
        setSaveError("Please log in to update listings");
        setIsSaving(false);
        return;
      }

      // Helper functions
      const cleanValue = (val) => (val === "" || val === undefined ? null : val);
      const cleanInt = (val) => {
        const parsed = parseInt(val);
        return isNaN(parsed) ? 0 : parsed;
      };
      const cleanFloat = (val) => {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? null : parsed;
      };

      // Prepare update payload
      const updatePayload = {
        status: formData.status || "active",
        transaction_type: formData.transaction_type || "buy",
        description: formData.description || "",
        basic_info: {
          address: cleanValue(formData.basic_info?.address),
          suburb: cleanValue(formData.basic_info?.suburb),
          postcode: cleanValue(formData.basic_info?.postcode),
          city: cleanValue(formData.basic_info?.city),
          state: cleanValue(formData.basic_info?.state) || "NSW",
          country: formData.basic_info?.country || "Australia",
          property_type: cleanValue(formData.basic_info?.property_type),
          headline: cleanValue(formData.basic_info?.headline),
          year_built: cleanInt(formData.basic_info?.year_built) || null,
          aspect: cleanValue(formData.basic_info?.aspect),
          coordinates: formData.basic_info?.coordinates?.lat && formData.basic_info?.coordinates?.lng 
            ? { lat: parseFloat(formData.basic_info.coordinates.lat), lng: parseFloat(formData.basic_info.coordinates.lng) }
            : null,
        },
        specifications: {
          bedrooms: cleanInt(formData.specifications?.bedrooms),
          bathrooms: cleanInt(formData.specifications?.bathrooms),
          ensuites: cleanInt(formData.specifications?.ensuites),
          toilets: cleanInt(formData.specifications?.toilets),
          car_spaces: cleanInt(formData.specifications?.car_spaces),
          car_parking_type: cleanValue(formData.specifications?.car_parking_type),
          land_size: cleanFloat(formData.specifications?.land_size),
          land_unit: formData.specifications?.land_unit || "sqm",
          floor_area: cleanFloat(formData.specifications?.floor_area),
          living_areas: cleanInt(formData.specifications?.living_areas) || 1,
        },
        features: {
          climate_control: formData.features?.climate_control || [],
          energy_efficiency: formData.features?.energy_efficiency || [],
          outdoor: formData.features?.outdoor || [],
          indoor: formData.features?.indoor || [],
          is_pet_friendly: formData.features?.is_pet_friendly || false,
        },
        pricing_and_financials: {
          display_price: cleanValue(formData.pricing_and_financials?.display_price),
          search_price: cleanFloat(formData.pricing_and_financials?.search_price),
          is_negotiable: formData.pricing_and_financials?.is_negotiable || false,
          council_rates: cleanFloat(formData.pricing_and_financials?.council_rates),
          water_rates: cleanFloat(formData.pricing_and_financials?.water_rates),
          strata_fees: cleanFloat(formData.pricing_and_financials?.strata_fees),
          zoning: cleanValue(formData.pricing_and_financials?.zoning),
        },
        location_context: {
          school_catchment: formData.location_context?.school_catchment || [],
          proximity: {
            transport: cleanValue(formData.location_context?.proximity?.transport),
            beach: cleanValue(formData.location_context?.proximity?.beach),
            shopping: cleanValue(formData.location_context?.proximity?.shopping),
            hospital: cleanValue(formData.location_context?.proximity?.hospital),
            parks: cleanValue(formData.location_context?.proximity?.parks),
          },
          walk_score: cleanInt(formData.location_context?.walk_score) || null,
        },
        ai_knowledge_base: {
          description: formData.ai_knowledge_base?.description || "",
          key_selling_points: formData.ai_knowledge_base?.key_selling_points || [],
          inspection_notes: formData.ai_knowledge_base?.inspection_notes || "",
        },
        media: {
          primary_image: cleanValue(formData.media?.primary_image),
          images: formData.media?.images || [],
          virtual_tour_url: cleanValue(formData.media?.virtual_tour_url),
          floor_plan_url: cleanValue(formData.media?.floor_plan_url),
          video_url: cleanValue(formData.media?.video_url),
        },
      };

      const response = await axios.put(
        `${BACKEND_URL}/api/listings/${listing.id}`,
        updatePayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "ok") {
        if (onListingUpdated) {
          onListingUpdated(listing.id);
        }
        handleClose();
      } else {
        setSaveError("Failed to update listing. Please try again.");
      }
    } catch (err) {
      console.error("Error updating listing:", err);
      if (err.response?.status === 401) {
        setSaveError("Session expired. Please log in again.");
      } else if (err.response?.status === 403) {
        setSaveError("Please log in to update listings.");
      } else if (err.response?.status === 404) {
        setSaveError("Listing not found.");
      } else {
        setSaveError(parseApiError(err));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData(getInitialFormData());
    setIsSaving(false);
    setSaveError("");
    setIsLoading(true);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0F1115] border-white/10 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-white/10 pb-4">
          <DialogTitle className="font-heading font-bold text-2xl text-[#F3F4F6] flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00D1FF]/10 flex items-center justify-center">
              <Edit3 className="w-5 h-5 text-[#00D1FF]" />
            </div>
            Edit Listing
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto mt-4 pr-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#00D1FF]" />
              <span className="ml-3 text-[#9CA3AF]">Loading listing data...</span>
            </div>
          ) : (
            <>
              {saveError && (
                <div className="flex items-center gap-2 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 mb-4">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{saveError}</p>
                </div>
              )}
              <EditManualMode
                formData={formData}
                setFormData={setFormData}
                onSave={handleUpdateListing}
                isSaving={isSaving}
              />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edit Manual Mode Component (similar to ManualMode but for editing)
const EditManualMode = ({ formData, setFormData, onSave, isSaving }) => {
  const updateBasicInfo = (field, value) => {
    setFormData({
      ...formData,
      basic_info: { ...formData.basic_info, [field]: value },
    });
  };

  const updateSpecifications = (field, value) => {
    setFormData({
      ...formData,
      specifications: { ...formData.specifications, [field]: value },
    });
  };

  const updateFeatures = (field, value) => {
    setFormData({
      ...formData,
      features: { ...formData.features, [field]: value },
    });
  };

  const updatePricing = (field, value) => {
    setFormData({
      ...formData,
      pricing_and_financials: { ...formData.pricing_and_financials, [field]: value },
    });
  };

  const updateLocationContext = (field, value) => {
    setFormData({
      ...formData,
      location_context: { ...formData.location_context, [field]: value },
    });
  };

  const updateProximity = (field, value) => {
    setFormData({
      ...formData,
      location_context: {
        ...formData.location_context,
        proximity: { ...formData.location_context.proximity, [field]: value },
      },
    });
  };

  const updateAIKnowledgeBase = (field, value) => {
    setFormData({
      ...formData,
      ai_knowledge_base: { ...formData.ai_knowledge_base, [field]: value },
    });
  };

  const updateMedia = (field, value) => {
    setFormData({
      ...formData,
      media: { ...formData.media, [field]: value },
    });
  };

  const propertyTypes = [
    { value: "House", label: "House" },
    { value: "Apartment", label: "Apartment" },
    { value: "Townhouse", label: "Townhouse" },
    { value: "Unit", label: "Unit" },
    { value: "Villa", label: "Villa" },
    { value: "Land", label: "Land" },
    { value: "Terrace", label: "Terrace" },
    { value: "Duplex", label: "Duplex" },
    { value: "Studio", label: "Studio" },
  ];

  const states = [
    { value: "NSW", label: "New South Wales" },
    { value: "VIC", label: "Victoria" },
    { value: "QLD", label: "Queensland" },
    { value: "WA", label: "Western Australia" },
    { value: "SA", label: "South Australia" },
    { value: "TAS", label: "Tasmania" },
    { value: "ACT", label: "Australian Capital Territory" },
    { value: "NT", label: "Northern Territory" },
  ];

  const aspects = [
    { value: "North-facing", label: "North-facing" },
    { value: "South-facing", label: "South-facing" },
    { value: "East-facing", label: "East-facing" },
    { value: "West-facing", label: "West-facing" },
    { value: "North-East", label: "North-East" },
    { value: "North-West", label: "North-West" },
    { value: "South-East", label: "South-East" },
    { value: "South-West", label: "South-West" },
  ];

  const parkingTypes = [
    { value: "Garage", label: "Garage" },
    { value: "Double Garage", label: "Double Garage" },
    { value: "Triple Garage", label: "Triple Garage" },
    { value: "Carport", label: "Carport" },
    { value: "Off-street", label: "Off-street" },
    { value: "On-street", label: "On-street" },
    { value: "Basement", label: "Basement Parking" },
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "under-offer", label: "Under Offer" },
    { value: "sold", label: "Sold" },
    { value: "withdrawn", label: "Withdrawn" },
    { value: "off-market", label: "Off Market" },
  ];

  return (
    <div className="space-y-6">
      {/* Status & Description */}
      <CollapsibleSection title="Status & Description" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            label="Listing Status"
            required
            options={statusOptions}
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            icon={Settings2}
          />
          <FormSelect
            label="Transaction Type"
            required
            options={[
              { value: "buy", label: "Buy (For Sale)" },
              { value: "rent", label: "Rent (For Lease)" },
            ]}
            value={formData.transaction_type || "buy"}
            onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
            icon={DollarSign}
          />
        </div>
        <FormTextarea
          label="General Description"
          placeholder="Brief description of the property..."
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
        />
      </CollapsibleSection>

      {/* Basic Info */}
      <CollapsibleSection title="Basic Information" icon={Home}>
        <FormInput
          label="Headline"
          required
          placeholder="e.g., Stunning Family Home with Ocean Views"
          value={formData.basic_info.headline || ""}
          onChange={(e) => updateBasicInfo("headline", e.target.value)}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormSelect
            label="Property Type"
            required
            options={propertyTypes}
            value={formData.basic_info.property_type || ""}
            onChange={(e) => updateBasicInfo("property_type", e.target.value)}
            icon={Building2}
          />
          <FormInput
            label="Year Built"
            type="number"
            placeholder="e.g., 1995"
            value={formData.basic_info.year_built || ""}
            onChange={(e) => updateBasicInfo("year_built", e.target.value)}
          />
        </div>
        <FormInput
          label="Street Address"
          required
          placeholder="e.g., 123 Bondi Rd"
          value={formData.basic_info.address || ""}
          onChange={(e) => updateBasicInfo("address", e.target.value)}
          icon={MapPin}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FormInput
            label="Suburb"
            required
            placeholder="Bondi"
            value={formData.basic_info.suburb || ""}
            onChange={(e) => updateBasicInfo("suburb", e.target.value)}
          />
          <FormInput
            label="Postcode"
            required
            placeholder="2026"
            value={formData.basic_info.postcode || ""}
            onChange={(e) => updateBasicInfo("postcode", e.target.value)}
          />
          <FormInput
            label="City"
            required
            placeholder="Sydney"
            value={formData.basic_info.city || ""}
            onChange={(e) => updateBasicInfo("city", e.target.value)}
          />
          <FormSelect
            label="State"
            required
            options={states}
            value={formData.basic_info.state || "NSW"}
            onChange={(e) => updateBasicInfo("state", e.target.value)}
          />
        </div>
        <FormSelect
          label="Aspect"
          options={aspects}
          value={formData.basic_info.aspect || ""}
          onChange={(e) => updateBasicInfo("aspect", e.target.value)}
          icon={Navigation}
          className="md:w-1/2"
        />
      </CollapsibleSection>

      {/* Specifications */}
      <CollapsibleSection title="Specifications" icon={Ruler}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FormInput
            label="Bedrooms"
            required
            type="number"
            placeholder="0"
            value={formData.specifications.bedrooms ?? ""}
            onChange={(e) => updateSpecifications("bedrooms", e.target.value)}
            icon={Bed}
          />
          <FormInput
            label="Bathrooms"
            required
            type="number"
            placeholder="0"
            value={formData.specifications.bathrooms ?? ""}
            onChange={(e) => updateSpecifications("bathrooms", e.target.value)}
            icon={Bath}
          />
          <FormInput
            label="Ensuites"
            type="number"
            placeholder="0"
            value={formData.specifications.ensuites ?? ""}
            onChange={(e) => updateSpecifications("ensuites", e.target.value)}
          />
          <FormInput
            label="Toilets"
            type="number"
            placeholder="0"
            value={formData.specifications.toilets ?? ""}
            onChange={(e) => updateSpecifications("toilets", e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <FormInput
            label="Car Spaces"
            type="number"
            placeholder="0"
            value={formData.specifications.car_spaces ?? ""}
            onChange={(e) => updateSpecifications("car_spaces", e.target.value)}
            icon={Car}
          />
          <FormSelect
            label="Parking Type"
            options={parkingTypes}
            value={formData.specifications.car_parking_type || ""}
            onChange={(e) => updateSpecifications("car_parking_type", e.target.value)}
          />
          <FormInput
            label="Living Areas"
            type="number"
            placeholder="1"
            value={formData.specifications.living_areas ?? ""}
            onChange={(e) => updateSpecifications("living_areas", e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <FormInput
            label="Land Size (sqm)"
            type="number"
            placeholder="0"
            value={formData.specifications.land_size ?? ""}
            onChange={(e) => updateSpecifications("land_size", e.target.value)}
          />
          <FormInput
            label="Floor Area (sqm)"
            type="number"
            placeholder="0"
            value={formData.specifications.floor_area ?? ""}
            onChange={(e) => updateSpecifications("floor_area", e.target.value)}
          />
        </div>
      </CollapsibleSection>

      {/* Features */}
      <CollapsibleSection title="Features & Amenities" icon={Zap} defaultOpen={false}>
        <FormInput
          label="Climate Control"
          placeholder="e.g., Ducted Air Conditioning, Fireplace (comma separated)"
          value={(formData.features.climate_control || []).join(", ")}
          onChange={(e) =>
            updateFeatures(
              "climate_control",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
        />
        <FormInput
          label="Energy Efficiency"
          placeholder="e.g., Solar Panels 5kW, Water Tank (comma separated)"
          value={(formData.features.energy_efficiency || []).join(", ")}
          onChange={(e) =>
            updateFeatures(
              "energy_efficiency",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
          icon={Zap}
        />
        <FormInput
          label="Outdoor Features"
          placeholder="e.g., In-ground Pool, Deck, Fully Fenced (comma separated)"
          value={(formData.features.outdoor || []).join(", ")}
          onChange={(e) =>
            updateFeatures(
              "outdoor",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
          icon={Trees}
        />
        <FormInput
          label="Indoor Features"
          placeholder="e.g., Built-in Wardrobes, Dishwasher, Floorboards (comma separated)"
          value={(formData.features.indoor || []).join(", ")}
          onChange={(e) =>
            updateFeatures(
              "indoor",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
        />
        <ToggleSwitch
          label="Pet Friendly"
          checked={formData.features.is_pet_friendly || false}
          onChange={(checked) => updateFeatures("is_pet_friendly", checked)}
          icon={Dog}
        />
      </CollapsibleSection>

      {/* Pricing & Financials */}
      <CollapsibleSection title="Pricing & Financials" icon={DollarSign} defaultOpen={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Display Price"
            required
            placeholder="e.g., $1,850,000 or Contact Agent"
            value={formData.pricing_and_financials.display_price || ""}
            onChange={(e) => updatePricing("display_price", e.target.value)}
            icon={DollarSign}
          />
          <FormInput
            label="Search Price (numeric)"
            type="number"
            placeholder="e.g., 1850000"
            value={formData.pricing_and_financials.search_price ?? ""}
            onChange={(e) => updatePricing("search_price", e.target.value)}
          />
        </div>
        <ToggleSwitch
          label="Price is Negotiable"
          checked={formData.pricing_and_financials.is_negotiable || false}
          onChange={(checked) => updatePricing("is_negotiable", checked)}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FormInput
            label="Council Rates ($/year)"
            type="number"
            placeholder="0"
            value={formData.pricing_and_financials.council_rates ?? ""}
            onChange={(e) => updatePricing("council_rates", e.target.value)}
          />
          <FormInput
            label="Water Rates ($/year)"
            type="number"
            placeholder="0"
            value={formData.pricing_and_financials.water_rates ?? ""}
            onChange={(e) => updatePricing("water_rates", e.target.value)}
          />
          <FormInput
            label="Strata Fees ($/quarter)"
            type="number"
            placeholder="0"
            value={formData.pricing_and_financials.strata_fees ?? ""}
            onChange={(e) => updatePricing("strata_fees", e.target.value)}
          />
          <FormInput
            label="Zoning"
            placeholder="e.g., R2"
            value={formData.pricing_and_financials.zoning || ""}
            onChange={(e) => updatePricing("zoning", e.target.value)}
          />
        </div>
      </CollapsibleSection>

      {/* Location Context */}
      <CollapsibleSection title="Location & Proximity" icon={MapPin} defaultOpen={false}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Nearest Transport"
            placeholder="e.g., 400m to Bondi Junction Station"
            value={formData.location_context.proximity?.transport || ""}
            onChange={(e) => updateProximity("transport", e.target.value)}
          />
          <FormInput
            label="Nearest Beach"
            placeholder="e.g., 1.2km to Bondi Beach"
            value={formData.location_context.proximity?.beach || ""}
            onChange={(e) => updateProximity("beach", e.target.value)}
          />
          <FormInput
            label="Nearest Shopping"
            placeholder="e.g., 600m to Westfield"
            value={formData.location_context.proximity?.shopping || ""}
            onChange={(e) => updateProximity("shopping", e.target.value)}
          />
          <FormInput
            label="Nearest Hospital"
            placeholder="e.g., 3km to St Vincent's"
            value={formData.location_context.proximity?.hospital || ""}
            onChange={(e) => updateProximity("hospital", e.target.value)}
          />
        </div>
        <FormInput
          label="Walk Score"
          type="number"
          placeholder="0-100"
          value={formData.location_context.walk_score ?? ""}
          onChange={(e) => updateLocationContext("walk_score", e.target.value)}
          className="md:w-1/4"
        />
      </CollapsibleSection>

      {/* AI Knowledge Base */}
      <CollapsibleSection title="AI Knowledge Base" icon={Brain} defaultOpen={false}>
        <FormTextarea
          label="Full Description (for AI/RAG)"
          placeholder="Detailed marketing description for AI to use..."
          value={formData.ai_knowledge_base.description || ""}
          onChange={(e) => updateAIKnowledgeBase("description", e.target.value)}
          rows={4}
        />
        <FormInput
          label="Key Selling Points"
          placeholder="e.g., Ocean views, Quiet cul-de-sac (comma separated)"
          value={(formData.ai_knowledge_base.key_selling_points || []).join(", ")}
          onChange={(e) =>
            updateAIKnowledgeBase(
              "key_selling_points",
              e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
            )
          }
        />
        <FormTextarea
          label="Inspection Notes (Private)"
          placeholder="Private notes for inspections..."
          value={formData.ai_knowledge_base.inspection_notes || ""}
          onChange={(e) => updateAIKnowledgeBase("inspection_notes", e.target.value)}
          rows={2}
        />
      </CollapsibleSection>

      {/* Media */}
      <CollapsibleSection title="Media & Images" icon={ImageIcon} defaultOpen={false}>
        <FormInput
          label="Primary Image URL"
          placeholder="https://..."
          value={formData.media.primary_image || ""}
          onChange={(e) => updateMedia("primary_image", e.target.value)}
          icon={ImageIcon}
        />
        <FormInput
          label="Floor Plan URL"
          placeholder="https://..."
          value={formData.media.floor_plan_url || ""}
          onChange={(e) => updateMedia("floor_plan_url", e.target.value)}
        />
        <FormInput
          label="Virtual Tour URL"
          placeholder="https://..."
          value={formData.media.virtual_tour_url || ""}
          onChange={(e) => updateMedia("virtual_tour_url", e.target.value)}
        />
        <FormInput
          label="Video URL"
          placeholder="https://..."
          value={formData.media.video_url || ""}
          onChange={(e) => updateMedia("video_url", e.target.value)}
          icon={Video}
        />
      </CollapsibleSection>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          className={`w-full flex items-center justify-center gap-2 py-4 rounded-lg font-heading font-bold transition-all ${
            isSaving
              ? "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
              : "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
          }`}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Main Listings Page Component
const Listings = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("buy");

  // Check if a listing is mock data
  const isMockListing = (listingId) => {
    return mockListings.some(mock => mock.id === listingId);
  };

  // Filter listings by transaction_type
  const buyListings = listings.filter(l => l.transaction_type === "buy");
  const rentListings = listings.filter(l => l.transaction_type === "rent");

  // Fetch user's listings from API
  const fetchListings = async () => {
    const token = getClientAuthToken();
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/listings/my_listings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "ok") {
        const apiListings = response.data.listings.map(listing => ({
          ...listing,
          basic_info: listing.basic_info || {},
          specifications: listing.specifications || {},
          pricing_and_financials: listing.pricing_and_financials || {},
          media: listing.media || {},
        }));
        
        setListings([...apiListings]);
      }
    } catch (err) {
      console.error("Error fetching listings:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch listings on component mount
  React.useEffect(() => {
    fetchListings();
  }, []);

  // Handle listing saved - refresh the list
  const handleListingSaved = (listingId) => {
    console.log("Listing saved:", listingId);
    fetchListings();
  };

  // Handle listing updated - refresh the list
  const handleListingUpdated = (listingId) => {
    console.log("Listing updated:", listingId);
    fetchListings();
  };

  // Handle view listing
  const handleViewListing = (listing) => {
    setSelectedListing(listing);
    setIsViewModalOpen(true);
  };

  // Handle edit listing
  const handleEditListing = (listing) => {
    setSelectedListing(listing);
    setIsEditModalOpen(true);
  };

  // Render a listings grid for a given array
  const renderListingsGrid = (listingsArr, emptyLabel) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#00D1FF]" />
        </div>
      );
    }

    if (listingsArr.length > 0) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listingsArr.map((listing) => (
            <ListingCard 
              key={listing.id} 
              listing={listing}
              onView={handleViewListing}
              onEdit={handleEditListing}
              isMockData={isMockListing(listing.id)}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
          <Home className="w-8 h-8 text-[#9CA3AF]" />
        </div>
        <h3 className="font-heading font-semibold text-lg text-[#F3F4F6] mb-2">
          {emptyLabel}
        </h3>
        <p className="text-[#9CA3AF] font-body text-sm mb-4">
          Click "Add New Property Listing" to create one.
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="listings-page">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 md:ml-64">
        <main className="p-6 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
                Property Listings
              </h1>
              <p className="text-[#9CA3AF] font-body">
                Manage your property portfolio
              </p>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#00D1FF] text-[#0F1115] font-heading font-bold hover:bg-[#00D1FF]/90 transition-colors"
              data-testid="add-listing-button"
            >
              <Plus className="w-5 h-5" />
              Add New Property Listing
            </button>
          </div>

          {/* Buy / Rent Section Tabs */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => setActiveSection("buy")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-heading font-semibold text-sm transition-all ${
                activeSection === "buy"
                  ? "bg-[#00D1FF] text-[#0F1115]"
                  : "bg-white/5 text-[#9CA3AF] hover:bg-white/10 hover:text-[#F3F4F6] border border-white/10"
              }`}
              data-testid="buy-tab"
            >
              <ShoppingCart className="w-4 h-4" />
              Buy
              {buyListings.length > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-mono ${
                  activeSection === "buy" ? "bg-[#0F1115]/20 text-[#0F1115]" : "bg-white/10 text-[#9CA3AF]"
                }`}>
                  {buyListings.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveSection("rent")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-heading font-semibold text-sm transition-all ${
                activeSection === "rent"
                  ? "bg-[#00D1FF] text-[#0F1115]"
                  : "bg-white/5 text-[#9CA3AF] hover:bg-white/10 hover:text-[#F3F4F6] border border-white/10"
              }`}
              data-testid="rent-tab"
            >
              <Key className="w-4 h-4" />
              Rent
              {rentListings.length > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-mono ${
                  activeSection === "rent" ? "bg-[#0F1115]/20 text-[#0F1115]" : "bg-white/10 text-[#9CA3AF]"
                }`}>
                  {rentListings.length}
                </span>
              )}
            </button>
          </div>

          {/* Section Content */}
          {activeSection === "buy" && (
            <div data-testid="buy-section">
              {renderListingsGrid(buyListings, "No Buy Listings Yet")}
            </div>
          )}

          {activeSection === "rent" && (
            <div data-testid="rent-section">
              {renderListingsGrid(rentListings, "No Rent Listings Yet")}
            </div>
          )}
        </main>
      </div>

      {/* Add Listing Modal */}
      <AddListingModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        onListingSaved={handleListingSaved}
      />

      {/* View Listing Modal */}
      <ViewListingModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedListing(null);
        }}
        listing={selectedListing}
        onEdit={!isMockListing(selectedListing?.id) ? handleEditListing : null}
      />

      {/* Edit Listing Modal */}
      <EditListingModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedListing(null);
        }}
        listing={selectedListing}
        onListingUpdated={handleListingUpdated}
      />
    </div>
  );
};

export default Listings;
